import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckSquare, Check, X, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SeedBadge } from "@/components/common/SeedBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Freigaben() {
  const { can } = useAuth();
  const writable = can("moderation", "write");
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/moderation", { params: { status: "pending" } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/moderation/${id}/${action}`, { comment: "" });
      toast.success(action === "approve" ? "Freigegeben" : "Abgelehnt");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="freigaben-page">
      <PageHeader title="Freigaben" subtitle="Einreichungen prüfen und freigeben oder ablehnen." />

      {items === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={CheckSquare} title="Keine offenen Freigaben" description="Alle Einreichungen sind bearbeitet." testid="freigaben-empty" />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {items.map((m) => (
            <div key={m.id} data-testid={`freigabe-card-${m.id}`} className="card-surface rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-fundio-ink">{m.title}<SeedBadge show={m.is_seed} /></p>
                  <p className="mt-0.5 text-xs text-fundio-faint">{m.brocki_name} · {m.ref_type} · {formatDate(m.created_at)}</p>
                  <p className="mt-1 text-xs text-fundio-muted">{m.reason}</p>
                </div>
                <span className="shrink-0 rounded-md border border-fundio-border px-2 py-1 font-mono text-xs text-fundio-muted">AI {(Number(m.ai_score) * 100).toFixed(0)}%</span>
              </div>
              {writable && (
                <div className="mt-4 flex gap-2">
                  <Button size="sm" data-testid={`freigabe-approve-${m.id}`} disabled={busyId === m.id} onClick={() => decide(m.id, "approve")} className="flex-1 bg-status-success-bg text-status-success hover:bg-status-success-border"><Check className="h-4 w-4" /> Freigeben</Button>
                  <Button size="sm" variant="ghost" data-testid={`freigabe-reject-${m.id}`} disabled={busyId === m.id} onClick={() => decide(m.id, "reject")} className="flex-1 border border-status-danger-border text-status-danger hover:bg-status-danger-bg"><X className="h-4 w-4" /> Ablehnen</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
